import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { TesseractScene } from './components/TesseractScene';
import { CelestialHud } from './components/CelestialHud';
import { MobileJoystick } from './components/MobileJoystick';
import { MobileRotationJoystick } from './components/MobileRotationJoystick';
import { useBtcPrice } from './hooks/useBtcPrice';
import { useMempoolBlock } from './hooks/useMempoolBlock';
import { useFxRates } from './hooks/useFxRates';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useEasterEggs } from './hooks/useEasterEggs';
import {
  priceDirectionAtom, newBlockTriggerAtom,
  feePressureAtom, mempoolFullnessAtom, blockTimestampAtom, isMutedAtom,
  milestoneAtom, satsModeAtom,
} from './state/atoms';
import { playBlockChime, updateAmbientTone, playMilestoneSound } from './utils/audio';

const BLOCK_VSIZE_MAX = 4_000_000;

export const App = () => {
  const { price, prevPrice, stats, loading } = useBtcPrice();
  const {
    block, blockHistory, fees, difficulty, mempoolInfo, mempoolTxCount, mempoolBlocks,
    loading: blockLoading, isNewBlock,
    vBytesPerSecond, trackedTxData, liveBlock0TxCount,
    trackTx, trackMempoolBlock,
  } = useMempoolBlock();
  const fxRates = useFxRates();

  const setPriceDirection = useSetRecoilState(priceDirectionAtom);
  const setNewBlockTrigger = useSetRecoilState(newBlockTriggerAtom);
  const setFeePressure = useSetRecoilState(feePressureAtom);
  const setMempoolFullness = useSetRecoilState(mempoolFullnessAtom);
  const setBlockTimestamp = useSetRecoilState(blockTimestampAtom);
  const isMuted = useRecoilValue(isMutedAtom);
  const setMilestone = useSetRecoilState(milestoneAtom);
  const satsMode = useRecoilValue(satsModeAtom);

  const triggerCount = useRecoilValue(newBlockTriggerAtom);
  const [showOverlay, setShowOverlay] = useState(false);
  const lastTriggerRef = React.useRef(0);
  // const [testBlocks, setTestBlocks] = useState([]);
  // const testHeightRef = useRef(0);

  const joystickRef = useRef({ x: 0, y: 0 });
  const rotationJoystickRef = useRef({ x: 0, y: 0 });
  const resetCameraRef = useRef(null);
  const cameraAchievementRef = useRef(null);
  const [hoveredCubeBlock, setHoveredCubeBlock] = useState(null);
  const [hoveredCubeScreenPos, setHoveredCubeScreenPos] = useState(null);
  const [isInsideCube, setIsInsideCube] = useState(false);

  /* const mergedBlockHistory = React.useMemo(() => {
    const all = [...testBlocks, ...blockHistory];
    all.sort((a, b) => b.height - a.height);
    return all.slice(0, 16);
  }, [blockHistory, testBlocks]);

  const handleTestBlock = useCallback(() => {
    const topHeight = mergedBlockHistory.length > 0 ? mergedBlockHistory[0].height : 900000;
    const newHeight = Math.max(topHeight + 1, testHeightRef.current + 1);
    testHeightRef.current = newHeight;
    const fakeBlock = {
      height: newHeight,
      timestamp: Math.floor(Date.now() / 1000),
      txCount: 1000 + Math.floor(Math.random() * 4000),
      size: 1500000,
      weight: 3900000,
    };
    setTestBlocks(prev => [fakeBlock, ...prev].slice(0, 8));
    setNewBlockTrigger(n => n + 1);
    if (!isMuted) playBlockChime();
  }, [mergedBlockHistory, setNewBlockTrigger, isMuted]); */

  const handleHoverBlock = useCallback((block, screenPos) => {
    setHoveredCubeBlock(block);
    setHoveredCubeScreenPos(screenPos ?? null);
  }, []);

  const handleJoystickMove = useCallback((x, y) => {
    joystickRef.current = { x, y };
  }, []);

  const handleRotationJoystickMove = useCallback((x, y) => {
    rotationJoystickRef.current = { x, y };
  }, []);

  useKeyboardShortcuts();
  const { handleLogoTap } = useEasterEggs(resetCameraRef);

  // Block milestone celebrations (every 100,000 blocks)
  useEffect(() => {
    if (block?.height && block.height % 100000 === 0) {
      setMilestone(true);
      if (!isMuted) playMilestoneSound();
      setTimeout(() => setMilestone(false), 4000);
    }
  }, [block?.height, setMilestone, isMuted]);

  useEffect(() => {
    if (triggerCount > lastTriggerRef.current) {
      lastTriggerRef.current = triggerCount;
      setShowOverlay(true);
      const timer = setTimeout(() => setShowOverlay(false), 3500);
      return () => clearTimeout(timer);
    }
  }, [triggerCount]);

  useEffect(() => {
    if (price && stats?.open) {
      const rawDir = price > stats.open ? 'up' : price < stats.open ? 'down' : 'flat';
      // In sats mode, direction flips: price up = fewer sats = bearish
      const dir = satsMode
        ? (rawDir === 'up' ? 'down' : rawDir === 'down' ? 'up' : 'flat')
        : rawDir;
      setPriceDirection(dir);
      updateAmbientTone(dir);
    }
  }, [price, stats, satsMode, setPriceDirection]);

  useEffect(() => {
    if (isNewBlock) {
      setNewBlockTrigger(n => n + 1);
      if (!isMuted) playBlockChime();
    }
  }, [isNewBlock, setNewBlockTrigger, isMuted]);

  useEffect(() => {
    if (fees?.fastestFee) {
      setFeePressure(Math.min(Math.log2(fees.fastestFee + 1) / 9, 1));
    }
  }, [fees, setFeePressure]);

  useEffect(() => {
    if (mempoolInfo?.vsize) {
      setMempoolFullness(Math.min(mempoolInfo.vsize / BLOCK_VSIZE_MAX, 1));
    }
  }, [mempoolInfo, setMempoolFullness]);

  useEffect(() => {
    if (block?.timestamp) setBlockTimestamp(block.timestamp);
  }, [block, setBlockTimestamp]);

  return (
    <Box position="relative" w="100vw" h="100vh" bg="black" overflow="hidden">
      <TesseractScene
        blockHistory={blockHistory}
        joystickRef={joystickRef}
        rotationJoystickRef={rotationJoystickRef}
        resetCameraRef={resetCameraRef}
        onHoverBlock={handleHoverBlock}
        onInsideCubeChange={setIsInsideCube}
        onCameraAchievement={(id) => cameraAchievementRef.current?.(id)}
        mempoolTxCount={mempoolInfo?.count || mempoolTxCount}
        difficulty={difficulty}
      />
      <CelestialHud
        price={price}
        prevPrice={prevPrice}
        stats={stats}
        loading={loading}
        block={block}
        blockHistory={blockHistory}
        blockLoading={blockLoading}
        fxRates={fxRates}
        isNewBlock={showOverlay}
        fees={fees}
        difficulty={difficulty}
        onResetCamera={() => resetCameraRef.current?.()}
        hoveredCubeBlock={hoveredCubeBlock}
        hoveredCubeScreenPos={hoveredCubeScreenPos}
        isInsideCube={isInsideCube}
        mempoolTxCount={mempoolInfo?.count || mempoolTxCount}
        mempoolBlocks={mempoolBlocks}
        vBytesPerSecond={vBytesPerSecond}
        trackedTxData={trackedTxData}
        liveBlock0TxCount={liveBlock0TxCount}
        trackTx={trackTx}
        trackMempoolBlock={trackMempoolBlock}
        onLogoTap={handleLogoTap}
        cameraAchievementRef={cameraAchievementRef}
      />
      <MobileJoystick onMove={handleJoystickMove} isActive />
      <MobileRotationJoystick onRotate={handleRotationJoystickMove} isActive />
      {/* <Box
        as="button"
        position="absolute"
        bottom="20px"
        left="20px"
        px={4}
        py={2}
        bg="orange.500"
        color="white"
        borderRadius="md"
        fontSize="sm"
        fontWeight="bold"
        zIndex={9999}
        opacity={0.8}
        _hover={{ opacity: 1, bg: 'orange.400' }}
        onClick={handleTestBlock}
      >
        Test Block
      </Box> */}
    </Box>
  );
};
